import { FC } from "react"

import { ReactComponent as IconQuestionMark } from "@common_assets/svg/question.svg"
import {
  Box,
  Skeleton,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material"
import { useMutationPostClassesStore } from "api/reactQuery/mutations/classesStore"
import { useQueryClassesStore } from "api/reactQuery/queries/classesStore"
import ErrorText from "components/common/error/errorText"
import IconWrapper from "components/common/icon/iconWrapper"
import ContentTabs from "components/common/navigation/contentTabs"
import { useClassId } from "hooks/navigation"
import { useCustomSnackbar } from "hooks/snackbar"
import { useTranslation } from "react-i18next"
import { useQueryClient } from "react-query"
import { Outlet } from "react-router"
import { getErrorMessage } from "utils/api"
import { transformRoutes } from "utils/tab"

import { storeRoutes } from "./TeacherStoreLayout.config"

const TeacherStoreLayout: FC = () => {
  const classId = useClassId()
  const tabs = transformRoutes(storeRoutes, ":classId", classId)
  const { t } = useTranslation()
  const { showSnackbar } = useCustomSnackbar()
  const queryClient = useQueryClient()

  const { data, isLoading, isError } = useQueryClassesStore({
    classId: Number(classId),
  })

  const { mutate, isLoading: isPostLoading } = useMutationPostClassesStore({
    options: {
      onMutate: async (newState) => {
        await queryClient.cancelQueries(["classesStore", Number(classId)])
        const prevValue = queryClient.getQueryData([
          "classesStore",
          Number(classId),
        ])
        queryClient.setQueryData(["classesStore", Number(classId)], newState)
        return prevValue
      },
      onSuccess: () => {
        showSnackbar({
          title: !data?.data.open
            ? t("store.classroomHasBeenOpened")
            : t("store.classroomHasBeenClosed"),
        })
      },
      onError: (error, _, prevValue) => {
        queryClient.setQueryData(["classesStore", Number(classId)], prevValue)
        showSnackbar({
          title: t(getErrorMessage(error)),
          variant: "error",
        })
      },
      onSettled: () => queryClient.invalidateQueries("classesStore"),
    },
  })

  return (
    <Box>
      <Stack
        mb="24px"
        justifyContent="space-between"
        alignItems={{ tablet: "center" }}
        direction={{ mobile: "column", tablet: "row" }}
        columnGap="12px"
        rowGap="16px"
      >
        <Typography
          component="h1"
          color="mockup.neutral10"
          fontSize="28px"
          lineHeight="36px"
        >
          {t("store.store")}
        </Typography>
        {isLoading && <Skeleton width="290px" height="26px" />}
        {!isLoading && isError && <ErrorText />}
        {!isLoading && data?.data && (
          <Stack
            direction="row"
            alignItems="center"
            gap="12px"
            justifyContent={{ mobile: "space-between", tablet: "normal" }}
          >
            <Stack
              direction={{ mobile: "row-reverse", tablet: "row" }}
              alignItems="center"
              gap="12px"
            >
              <Typography
                id="classroomStoreToggleLabel"
                variant="subtitle1"
                color="primary.main"
                letterSpacing="0.16px"
                lineHeight="16px"
                fontSize="16px"
                textTransform="uppercase"
              >
                {data?.data.open
                  ? t("store.classroomStoreOpen")
                  : t("store.classroomStoreClosed")}
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="center" gap="12px">
              <Switch
                disabled={isPostLoading}
                checked={data?.data.open}
                slotProps={{
                  input: {
                    "aria-labelledby": "classroomStoreToggleLabel",
                  },
                }}
                onChange={(e) => {
                  mutate({
                    classId: Number(classId),
                    data: { open: e.target.checked },
                  })
                }}
              />
              <IconWrapper>
                <Tooltip
                  title={t("store.whenTheClassroomStoreIsOpen")}
                  componentsProps={{
                    tooltip: {
                      sx: {
                        color: "mockup.grey70",
                      },
                    },
                  }}
                >
                  <IconQuestionMark />
                </Tooltip>
              </IconWrapper>
            </Stack>
          </Stack>
        )}
      </Stack>
      <Box height="43px" width="100%" position="relative">
        <Box position="absolute" width="100%" top="0" left="0">
          <ContentTabs tabs={tabs} />
        </Box>
      </Box>
      <Outlet />
    </Box>
  )
}

export default TeacherStoreLayout
